import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { StoreState } from '../../state/configureStore'
import { AnyAction, Dispatch } from 'redux'
import { renameMachine } from '../../state/actions/machine'
import { Text } from 'recharts'
import { MachineDetails } from './machineDetails'
import { SaveModal } from '../../components/molecules/SavingModal'
import { PostToBackoffice } from '../../utils/commands'
import { FetchFromBackoffice } from '../../utils/request'

type IComponentProps = IComponentOwnProps & IComponentStoreProps & IComponentDispatchProps;

type IComponentStoreProps = ReturnType<typeof mapStateToProps>;
type IComponentDispatchProps = ReturnType<typeof mapDispatchToProps>;

interface IComponentOwnProps {
  open: boolean;
  closeModal: Function;
  details: MachineDetails | undefined;
}

type FactoryResetResult = {
  success: boolean;
  executing: boolean;
  message: string | undefined;
  lastReset: string | undefined;
}

function VerifyFactoryResetModal(props: IComponentProps): React.ReactElement {
  const [savingStatus, setSavingStatus] = useState<boolean | null>(null)
  const [savingFailed, setSavingFailed] = useState<boolean | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | undefined>(undefined)

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!props?.details?.serialNumber) return;

    FetchFromBackoffice<FactoryResetResult>(`machines/${props.details.serialNumber}/is-factory-reset`)
      .then(r => {

        if (!r?.lastReset && !r?.executing) {
          setSavingStatus(false);
          setSavingFailed(null);
          setStatusMessage(undefined);
          return;
        }

        const formattedDate = r.lastReset 
          ? new Date(r.lastReset).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
          : 'an unknown time';

        if (r.executing) {
          setSavingStatus(true);
          setSavingFailed(null); 
          setStatusMessage(`Ongoing factory reset, started at ${formattedDate}. Info: ${r.message || 'Processing...'}`);
          pollStatus(new Date().toISOString())
        } 
        else if (r.success) {
          setSavingStatus(false);
          setSavingFailed(false);
          setStatusMessage(`Factory reset successful on ${formattedDate}.`);
        } 
        else {
          setSavingStatus(false);
          setSavingFailed(true);
          setStatusMessage(`Factory reset on ${formattedDate} failed. ${r.message || ''}`);
          }
      })
      .catch(() => {
          setSavingStatus(false);
          setSavingFailed(true); 
          setStatusMessage("Could not connect to the server to verify the current reset status.");
      });
    
  }, [props?.details?.serialNumber]);

  const pollStatus = async (since: string) => {
    if (!isMounted.current || !props.details?.serialNumber) return;

    try {
      const result = await FetchFromBackoffice<FactoryResetResult>(
        `machines/${props.details.serialNumber}/is-factory-reset?since=${encodeURIComponent(since)}`
      );

      if (result.success) {
        setSavingStatus(false);
        setSavingFailed(false);
        setStatusMessage(undefined);
        return;
      }

      if (!result.executing && !result.success) {
        setSavingStatus(false);
        setSavingFailed(true);
        console.error("Error when factory resetting hub with serial number" + props.details.serialNumber + ". Message: " + result.message)
        setStatusMessage("Factory reset failed! " + result.message)
        return;
      }

      if (result.executing) {
        setSavingStatus(true);
        setSavingFailed(null); 
        setStatusMessage(`Ongoing factory reset. Info: ${result?.message || 'Processing...'}`);
        setTimeout(() => pollStatus(since), 5000);
      }
    } catch (error) {
      setSavingStatus(false);
      setSavingFailed(true);
      setStatusMessage("Factory reset failed!");
    }
  }

  const onVerify = async () => {
    if (!props.details?.serialNumber) return;

    setSavingStatus(true);
    setSavingFailed(null);
    setStatusMessage(undefined);

    const startTime = new Date().toISOString();

    try {
      const res = await PostToBackoffice("cmd/machine/factory-reset", {
        id: props.details.serialNumber, 
        serialNumber: props.details.serialNumber
      });

      if (res.message && res.message.includes("failed")) {
         setSavingStatus(false);
         setSavingFailed(true);
         return;
      }

      pollStatus(startTime);

    } catch (error) {
      setSavingStatus(false);
      setSavingFailed(true);
    }
  }

  return (
    <SaveModal
      title={'Factory reset machine'}
      open={props.open}
      savingFailedMessage={statusMessage || 'Factory reset failed! Try again later.'}
      savingOngoingMessage={statusMessage}
      savingSuccessMessage={statusMessage || 'Factory reset successful!'}
      saveButtonText={'Reset'}
      closeModal={() => { props.closeModal() }} 
      onSave={onVerify}
      saving={savingStatus ?? true}
      savingFailed={savingFailed === true}
      savingDisabled={savingStatus ?? true}
      savingSuccessful={savingFailed === false}
      savingOngoing={savingStatus ?? false}
      setSavingFailed={setSavingFailed}>
      <Text> 
        This action will factory reset the machine, removing all installed recipes and resetting the PIN. 
        Please ensure the affected store is notified beforehand and that the hub is online. 
        This process runs in the background and will retry for up to an hour if it encounters issues, so you can safely close this window.
      </Text>
    </SaveModal>
  )
}

const mapStateToProps = (state: StoreState) => ({
  machine_saving: state.machine_saving
})

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  renameMachine: (id: string, name: string) => dispatch(renameMachine(id, name))
})

export default connect(mapStateToProps, mapDispatchToProps)(VerifyFactoryResetModal)